import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, LogOut, Mail, Phone, StickyNote } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChatPanel } from "@/components/ChatPanel";
import {
  PageContainer, PageHeader, StatusPill, LEAD_STATUS, Skeleton, formatDateTime, relativeTime,
} from "./bdcUi";
import BdcLeadPhonePanel from "./BdcLeadPhonePanel";
import LeadToolCards from "@/components/LeadToolCards";
import BdcLeadCallHistory from "@/components/BdcLeadCallHistory";
import LeadClaimChip from "@/components/LeadClaimChip";
import { useAuth } from "@/context/AuthContext";

const LEAD_TRANSITIONS = {
  new: ["contacted", "lost", "spam"],
  contacted: ["followed_up", "lost", "spam"],
  followed_up: ["lost", "spam"],
  lost: [],
  spam: [],
};

const BookAppointmentDialog = ({ open, onOpenChange, leadId, onBooked }) => {
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
      d.setSeconds(0, 0);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setWhen(iso);
      setNotes("");
    }
  }, [open]);

  const submit = async () => {
    if (!when) return;
    setSaving(true);
    try {
      const scheduled_at = new Date(when).toISOString();
      const { data } = await api.post("/bdc/appointments", {
        lead_id: leadId, scheduled_at, duration_minutes: Number(duration), notes: notes || null,
      });
      toast.success("Appointment booked and emails queued");
      onBooked?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="book-appt-dialog">
        <DialogHeader>
          <DialogTitle>Book appointment</DialogTitle>
          <DialogDescription>Customer + dealer will be emailed instantly. Reminders auto-scheduled for 24h before.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="appt-when">When</Label>
            <Input id="appt-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="book-appt-when-input" />
          </div>
          <div>
            <Label htmlFor="appt-dur">Duration (min)</Label>
            <Input id="appt-dur" type="number" min="10" step="5" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="book-appt-duration-input" />
          </div>
          <div>
            <Label htmlFor="appt-notes">Notes (optional)</Label>
            <Textarea id="appt-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="book-appt-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="bg-brand text-white hover:bg-brand-hover" onClick={submit} disabled={saving || !when} data-testid="book-appt-confirm-btn">
            {saving ? "Booking…" : "Confirm booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BdcLeadWorkspace = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookOpen, setBookOpen] = useState(false);
  const [statusPending, setStatusPending] = useState(false);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bdc/leads/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const changeStatus = async (target) => {
    if (!detail || detail.status === target) return;
    if (["lost", "spam"].includes(target)) {
      // Phase 11 — mandatory notes dialog for lost / spam transitions.
      const reason = window.prompt(
        `Reason for "${LEAD_STATUS[target].label}"? (required)\n\nBackend rejects the transition without a note.`,
      );
      if (!reason || !reason.trim()) {
        toast.error("Notes are required to mark this lead as lost or spam.");
        return;
      }
      setStatusPending(true);
      try {
        await api.patch(`/bdc/leads/${id}`, { status: target, close_reason: reason.trim() });
        toast.success("Status updated");
        setRefreshTick((n) => n + 1);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setStatusPending(false); }
      return;
    }
    setStatusPending(true);
    try {
      await api.patch(`/bdc/leads/${id}`, { status: target });
      toast.success("Status updated");
      setRefreshTick((n) => n + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStatusPending(false); }
  };

  const release = async () => {
    if (!window.confirm("Return this lead to the shared queue?")) return;
    try {
      await api.post(`/bdc/leads/${id}/release`);
      toast.success("Lead released to queue");
      navigate("/bdc/queue");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const claim = async () => {
    try {
      await api.post(`/bdc/leads/${id}/claim`);
      toast.success("Claimed");
      setRefreshTick((n) => n + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      await api.post(`/bdc/leads/${id}/notes`, { body: note.trim() });
      setNote("");
      toast.success("Note added");
      setRefreshTick((n) => n + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  if (loading || !detail) {
    return (
      <PageContainer>
        <PageHeader title="Lead workspace" subtitle="Loading…" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[0,1,2].map((i) => <Skeleton key={i} className="h-96" />)}
        </div>
      </PageContainer>
    );
  }

  const v = detail.vehicle;
  const d = detail.dealer;
  const contactName = detail.customer?.full_name || detail.guest_name || "Guest";
  const contactEmail = detail.customer?.email || detail.guest_email;
  const contactPhone = detail.customer?.phone || detail.guest_phone;
  const allowedTargets = LEAD_TRANSITIONS[detail.status] || [];

  const messagesEndpoint = async (after) => {
    if (!detail.chat_thread_id) return { data: { messages: [] } };
    if (!after) {
      const r = await api.get(`/bdc/threads/${detail.chat_thread_id}`);
      return { data: { messages: r.data.messages || [] } };
    }
    return api.get(`/bdc/threads/${detail.chat_thread_id}/messages`, { params: { after } });
  };
  const sendEndpoint = (body) => api.post(`/bdc/threads/${detail.chat_thread_id}/messages`, { body });

  // Phase 9 claim-lock — read-only view when someone else holds the claim.
  const claimedByOther = detail.assigned_bdc_id && detail.assigned_bdc_id !== user?.id;
  const claimantName = detail.assigned_bdc_name || detail.assigned_bdc?.full_name;

  return (
    <PageContainer>
      <PageHeader
        title={contactName}
        subtitle={v ? `${v.year} ${v.make} ${v.model} · ${formatUSD(v.price)} · ${d?.name || ""}` : (d?.name || "")}
        testId="bdc-workspace-header"
        right={
          <>
            <Button asChild variant="ghost" className="text-slate-500"><Link to="/bdc/queue" data-testid="bdc-back-queue-btn"><ArrowLeft size={15} /> Queue</Link></Button>
            {!detail.assigned_bdc_id && (
              <Button onClick={claim} className="bg-brand text-white hover:bg-brand-hover" data-testid="bdc-claim-btn"><StickyNote size={14} /> Claim</Button>
            )}
            {detail.assigned_bdc_id && !claimedByOther && (
              <Button variant="outline" onClick={release} data-testid="bdc-release-btn"><LogOut size={14} /> Release</Button>
            )}
            <Button
              onClick={() => setBookOpen(true)}
              disabled={claimedByOther}
              className="bg-gold text-white hover:bg-gold-hover disabled:opacity-40"
              data-testid="bdc-book-appt-btn"
              title={claimedByOther ? "Claimed by another agent — request handoff first." : undefined}
            >
              <CalendarPlus size={15} /> Book appointment
            </Button>
          </>
        }
      />

      {claimedByOther && (
        <div className="mb-4">
          <LeadClaimChip
            lead={detail}
            claimantName={claimantName}
            claimantId={detail.assigned_bdc_id}
            vehicle={v}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column — contact + vehicle + status */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-workspace-contact">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Contact</p>
            <p className="font-semibold text-slate-800">{contactName}</p>
            {contactEmail && <p className="text-sm text-slate-500 flex items-center gap-2 mt-1"><Mail size={13} className="text-slate-400" /> {contactEmail}</p>}
            {contactPhone && <p className="text-sm text-slate-500 flex items-center gap-2"><Phone size={13} className="text-slate-400" /> {contactPhone}</p>}
            <StatusPill tone={detail.customer_id ? "blue" : "slate"} className="mt-2">{detail.customer_id ? "Registered" : "Guest"}</StatusPill>
          </div>

          {v && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-workspace-vehicle">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Vehicle</p>
              {/* Phase 11 — clickable vehicle chip jumps to the marketplace
                  detail page for quick research. Same tab per user brief. */}
              <Link
                to={`/vehicle/${v.id}`}
                className="flex gap-3 rounded-lg -m-1 p-1 hover:bg-slate-50 transition-colors group"
                data-testid="bdc-workspace-vehicle-link"
              >
                <div className="w-20 h-14 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
                  {v.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate group-hover:text-brand">
                    {v.year} {v.make} {v.model} {v.trim || ""}
                  </p>
                  <p className="text-xs text-slate-500">{formatUSD(v.price)}</p>
                  <p className="text-[11px] text-slate-500">
                    {d?.name}{v.stock_number && <span className="ml-1 font-mono text-slate-400">· #{v.stock_number}</span>}
                  </p>
                </div>
              </Link>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-workspace-status">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Lead status</p>
            <div className="flex items-center gap-2">
              <StatusPill tone={LEAD_STATUS[detail.status]?.tone || "slate"}>{LEAD_STATUS[detail.status]?.label}</StatusPill>
            </div>
            {allowedTargets.length > 0 && (
              <Select
                onValueChange={(v) => changeStatus(v)}
                disabled={statusPending}
              >
                <SelectTrigger className="mt-3" data-testid="bdc-status-select"><SelectValue placeholder="Move to…" /></SelectTrigger>
                <SelectContent>
                  {allowedTargets.map((t) => (
                    <SelectItem key={t} value={t}>{LEAD_STATUS[t].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {detail.close_reason && (
              <p className="mt-3 text-xs text-slate-500 italic">"{detail.close_reason}"</p>
            )}
          </div>
        </div>

        {/* Center column — chat */}
        <div className="lg:col-span-1" data-testid="bdc-workspace-chat">
          {detail.chat_thread_id ? (
            <ChatPanel
              viewerType="bdc"
              messagesEndpoint={messagesEndpoint}
              sendEndpoint={sendEndpoint}
              testIdPrefix="bdc-chat"
              emptyStateText="Start the conversation — customer will get an in-app notification."
            />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-slate-500 text-sm h-full flex items-center justify-center">
              No chat thread linked to this lead yet.
            </div>
          )}
        </div>

        {/* Right column — notes + appointments */}
        <div className="space-y-4">
          <BdcLeadPhonePanel leadId={id} contactPhone={contactPhone} onSmsSent={() => setRefreshTick((n) => n + 1)} />

          {/* Phase 7 — financing/trade-in context cards (only render if lead has them) */}
          <LeadToolCards lead={detail} />

          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-workspace-notes">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Internal notes</p>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {(detail.bdc_notes || []).length === 0 && (detail.dealer_notes || []).length === 0 ? (
                <p className="text-sm text-slate-500">No notes yet.</p>
              ) : (
                [...(detail.bdc_notes || []).map(n => ({ ...n, tag: "BDC" })), ...(detail.dealer_notes || []).map(n => ({ ...n, tag: "Dealer" }))]
                  .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                  .map((n) => (
                    <div key={n.id} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-700">{n.author_name} <span className="ml-1 text-[10px] text-slate-400">· {n.tag}</span></p>
                        <p className="text-[11px] text-slate-400">{relativeTime(n.created_at)}</p>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-line">{n.body}</p>
                    </div>
                  ))
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add internal note…" data-testid="bdc-note-input" />
              <Button className="bg-brand text-white hover:bg-brand-hover self-end" disabled={posting || !note.trim()} onClick={addNote} data-testid="bdc-note-add-btn">
                {posting ? "Saving…" : "Add note"}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-workspace-appts">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Appointments</p>
            {(detail.appointments || []).length === 0 ? (
              <p className="text-sm text-slate-500">No appointments yet. Use "Book appointment" above.</p>
            ) : (
              <div className="space-y-2">
                {detail.appointments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                    <span className="text-slate-700">{formatDateTime(a.scheduled_at)}</span>
                    <StatusPill tone={a.status === "showed_up" ? "emerald" : a.status === "no_show" ? "rose" : "amber"}>
                      {a.status.replace("_", " ")}
                    </StatusPill>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Phase 11 — call history for this lead's phone */}
          <BdcLeadCallHistory leadId={id} />
        </div>
      </div>

      <BookAppointmentDialog open={bookOpen} onOpenChange={setBookOpen} leadId={id} onBooked={() => setRefreshTick((n) => n + 1)} />
    </PageContainer>
  );
};

export default BdcLeadWorkspace;
